import { ComponentType, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Spinner, Tag, TagPicker, TagPickerControl, TagPickerGroup, TagPickerInput, TagPickerList, TagPickerOption, TagPickerProps } from '@fluentui/react-components';
import { Settings16Regular } from '@fluentui/react-icons';

import * as ComponentFunctions from './ContractConceptCategorySelector.Functions';
import { ContractConceptCategory } from '../../../../models/Entities/Contracts/ContractConceptCategory';
import ContractConceptCategoriesDrawer from '../../../../components/Contracts/Concept/ContractConceptCategories/ContractConceptCategories.Drawer.Component';

import './ContractConceptCategorySelector.Styles.css';

/**
 * Selector de categoría de concepto de contrato. Una sola por defecto; con
 * `MultiselectEnable` admite varias. El engranaje abre el drawer para
 * administrarlas sin salir del formulario.
 */
const ContractConceptCategorySelector = (props: ComponentFunctions.Props) => {
    const [isManageOpen, setIsManageOpen] = useState(false);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!props.hasLoaded && !props.isLoading) props.GetAllContractConceptCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = useMemo(() => ComponentFunctions.resolveSelected(props.SelectedCategories, props.Categories), [props.SelectedCategories, props.Categories]);
    const multi = props.MultiselectEnable === true;

    const options = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return props.Categories
            .filter((category) => !selected.some((item) => item.id === category.id))
            .filter((category) => !needle || (category.name || '').toLowerCase().includes(needle));
    }, [props.Categories, selected, query]);

    const onOptionSelect: TagPickerProps['onOptionSelect'] = (_, data) => {
        const id = Number(data.value);
        const isRemoval = !data.selectedOptions.includes(data.value);
        let next: ContractConceptCategory[];
        if (isRemoval) {
            next = selected.filter((item) => item.id !== id);
        } else {
            const picked = props.Categories.find((item) => item.id === id);
            if (!picked) return;
            next = multi ? [...selected, picked] : [picked];
        }
        setQuery('');
        props.onSelectedCategories(next);
    };

    const showLabel = props.showLabel !== false;
    const label = props.label ?? 'Categoría de concepto';

    return (
        <div className={`cccs-root${props.errorMessage ? ' cccs-root--error' : ''}`}>
            {showLabel && (
                <label className="cccs-label">
                    {label}{props.required && <span className="cccs-required"> *</span>}
                </label>
            )}
            <div className="cccs-control">
                <div className="cccs-picker">
                    <TagPicker
                        onOptionSelect={onOptionSelect}
                        selectedOptions={selected.map((item) => String(item.id))}
                        disabled={props.disabled}
                        inline={props.inlineList}
                    >
                        <TagPickerControl expandIcon={props.isLoading ? <Spinner size="extra-tiny" /> : undefined}>
                            <TagPickerGroup aria-label="Categorías seleccionadas">
                                {selected.map((item) => (
                                    <Tag key={item.id} shape="rounded" value={String(item.id)} title={item.description || item.name}>
                                        {item.name}
                                    </Tag>
                                ))}
                            </TagPickerGroup>
                            <TagPickerInput
                                aria-label={label}
                                placeholder={selected.length === 0 ? 'Elegí una categoría' : (multi ? 'Agregar otra…' : '')}
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                            />
                        </TagPickerControl>
                        <TagPickerList>
                            {options.length > 0 ? options.map((item) => (
                                <TagPickerOption key={item.id} value={String(item.id)} text={item.name} secondaryContent={item.description || undefined}>
                                    {item.name}
                                </TagPickerOption>
                            )) : (
                                <div className="cccs-empty">
                                    {props.failOnLoading
                                        ? 'No se pudieron cargar las categorías.'
                                        : props.Categories.length === 0 ? 'Todavía no hay categorías' : 'Sin coincidencias'}
                                </div>
                            )}
                        </TagPickerList>
                    </TagPicker>
                </div>
                {props.showManageButton !== false && (
                    <button type="button" className="cccs-manage" title="Administrar categorías" aria-label="Administrar categorías" onClick={() => setIsManageOpen(true)} disabled={props.disabled}>
                        <Settings16Regular />
                    </button>
                )}
            </div>
            {props.errorMessage
                ? <span className="cccs-error">{props.errorMessage}</span>
                : props.hint && <span className="cccs-hint">{props.hint}</span>}

            {isManageOpen && (
                <ContractConceptCategoriesDrawer isOpen={isManageOpen} onClose={() => setIsManageOpen(false)} />
            )}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ContractConceptCategorySelector as any) as ComponentType<ComponentFunctions.IProps>;
